import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import config from '../config';
import { useIsMobile } from '../hooks/useMediaQuery';

// Normalize for search: strip diacritics and punctuation so "motorhead"
// matches "Motörhead" and "carissas" matches "Carissa's Wierd".
const normalizeStr = (str) =>
    (str || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/['\u2018\u2019\u201b`\-.,:!?&]/g, '')
        .toLowerCase();

const styles = {
    section: { marginBottom: '32px' },
    sectionHeader: {
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '12px 16px', backgroundColor: '#1e1e1e', borderRadius: '8px',
        cursor: 'pointer', border: '1px solid #333', marginBottom: '2px'
    },
    sectionTitle: { fontSize: '16px', fontWeight: 600, color: '#bb86fc', margin: 0 },
    sectionCount: { color: '#666', fontSize: '13px' },
    chevron: { color: '#666', fontSize: '16px', transition: 'transform 0.2s' },
    sectionBody: { border: '1px solid #333', borderTop: 'none', borderRadius: '0 0 8px 8px', padding: '16px', backgroundColor: '#161616' },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: { textAlign: 'left', padding: '8px 12px', borderBottom: '2px solid #333', color: '#aaa', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' },
    td: { padding: '10px 12px', borderBottom: '1px solid #222', fontSize: '14px', verticalAlign: 'middle' },
    link: { color: '#bb86fc', textDecoration: 'none' },
    badge: { display: 'inline-block', backgroundColor: '#2a2a2a', color: '#aaa', borderRadius: '12px', padding: '2px 10px', fontSize: '12px' },
    rolePill: { display: 'inline-block', backgroundColor: '#1e1240', color: '#bb86fc', borderRadius: '12px', padding: '2px 10px', fontSize: '12px', border: '1px solid #3d2a7a', marginRight: '3px' },
    expandBtn: { background: 'none', border: 'none', color: '#bb86fc', cursor: 'pointer', fontSize: '12px', padding: '0' },
    expandedShows: { backgroundColor: '#1a1a2e', borderRadius: '6px', padding: '10px 14px', marginTop: '4px' },
    showRow: { display: 'flex', gap: '16px', padding: '6px 0', borderBottom: '1px solid #222', fontSize: '13px', color: '#ccc', alignItems: 'center', flexWrap: 'wrap' },
    emptyState: { color: '#666', padding: '24px 0', textAlign: 'center', fontSize: '14px' },
    lineupCard: { border: '1px solid #333', borderRadius: '8px', padding: '14px 16px', marginBottom: '12px', backgroundColor: '#1a1a1a' },
    lineupName: { fontWeight: 600, color: '#e0e0e0', marginBottom: '8px', fontSize: '15px' },
    memberPill: { display: 'inline-flex', alignItems: 'center', gap: '6px', backgroundColor: '#2a2a2a', border: '1px solid #444', borderRadius: '20px', padding: '4px 12px', fontSize: '13px', marginRight: '6px', marginBottom: '6px' },
    lineupActions: { display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '8px' },
    deleteBtn: { background: 'none', border: '1px solid #555', borderRadius: '4px', color: '#cf6679', cursor: 'pointer', fontSize: '12px', padding: '4px 10px' },
    editBtn: { background: 'none', border: '1px solid #555', borderRadius: '4px', color: '#bb86fc', cursor: 'pointer', fontSize: '12px', padding: '4px 10px' },
    addLineupForm: { border: '1px dashed #444', borderRadius: '8px', padding: '16px', marginTop: '12px', backgroundColor: '#1a1a1a' },
    editLineupForm: { border: '1px solid #bb86fc44', borderRadius: '8px', padding: '16px', backgroundColor: '#1a1a2e' },
    formRow: { display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '10px' },
    input: { flex: 1, padding: '8px 12px', backgroundColor: '#2a2a2a', border: '1px solid #444', borderRadius: '6px', color: '#e0e0e0', fontSize: '14px' },
    smallInput: { padding: '6px 10px', backgroundColor: '#2a2a2a', border: '1px solid #444', borderRadius: '6px', color: '#e0e0e0', fontSize: '13px' },
    addRowBtn: { background: 'none', border: '1px dashed #555', borderRadius: '6px', color: '#bb86fc', padding: '6px 14px', cursor: 'pointer', fontSize: '13px' },
    saveBtn: { backgroundColor: '#bb86fc', color: '#000', border: 'none', borderRadius: '6px', padding: '8px 18px', fontWeight: 600, cursor: 'pointer', fontSize: '13px' },
    cancelBtn: { background: 'none', border: '1px solid #444', borderRadius: '6px', color: '#aaa', padding: '8px 14px', cursor: 'pointer', fontSize: '13px' },
    removeBtn: { background: 'none', border: 'none', color: '#cf6679', cursor: 'pointer', fontSize: '16px', padding: '0 4px' },
    showsCheckList: { maxHeight: '400px', overflowY: 'auto' },
    checkRow: { display: 'flex', alignItems: 'center', gap: '12px', padding: '8px 4px', borderBottom: '1px solid #222', fontSize: '14px' },
    checkbox: { width: '16px', height: '16px', cursor: 'pointer', accentColor: '#bb86fc' },
    assignBar: { display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap' },
    select: { flex: 1, minWidth: '200px', padding: '8px 12px', backgroundColor: '#2a2a2a', border: '1px solid #444', borderRadius: '6px', color: '#e0e0e0', fontSize: '14px' },
    successMsg: { color: '#4caf50', fontSize: '13px', padding: '6px 0' },
    errorMsg: { color: '#cf6679', fontSize: '13px', padding: '6px 0' },
    memberSelect: { padding: '8px 12px', backgroundColor: '#2a2a2a', border: '1px solid #444', borderRadius: '6px', color: '#e0e0e0', fontSize: '14px' },
    showEditPanel: { backgroundColor: '#12121e', border: '1px solid #3d2a7a', borderRadius: '6px', padding: '12px 14px', marginTop: '6px' },
    showEditTitle: { fontSize: '12px', color: '#bb86fc', fontWeight: 600, marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.05em' },
    subTh: { textAlign: 'left', padding: '6px 10px', borderBottom: '1px solid #2a2a2a', color: '#888', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' },
    subTd: { padding: '7px 10px', borderBottom: '1px solid #1e1e2e', fontSize: '13px', color: '#ccc', verticalAlign: 'middle' },
};

// Combined searchable member picker + inline quick-create modal
function MemberSearchSelect({ value, allMembers, onChange, onMemberCreated }) {
    const [search, setSearch] = useState('');
    const [open, setOpen] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [modalName, setModalName] = useState('');
    const ref = React.useRef(null);

    const selected = allMembers && allMembers.find(m => String(m.id) === String(value));

    React.useEffect(() => {
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const filtered = (allMembers || []).filter(m =>
        !search || normalizeStr(m.name).includes(normalizeStr(search))
    );

    const handleOpenModal = () => {
        setModalName(search);
        setOpen(false);
        setShowModal(true);
    };

    const handleCreated = (newMember) => {
        setShowModal(false);
        setSearch('');
        onChange(String(newMember.id));
        if (onMemberCreated) onMemberCreated(newMember);
    };

    const dropStyle = {
        position: 'absolute', zIndex: 200, top: '100%', left: 0, right: 0,
        backgroundColor: '#2a2a2a', border: '1px solid #555', borderRadius: '6px',
        maxHeight: '220px', overflowY: 'auto', marginTop: '2px', boxShadow: '0 4px 12px rgba(0,0,0,0.4)'
    };
    const optStyle = (active) => ({
        padding: '8px 12px', cursor: 'pointer', fontSize: '13px',
        color: active ? '#bb86fc' : '#e0e0e0',
        backgroundColor: active ? '#1e1240' : 'transparent',
        borderBottom: '1px solid #333'
    });

    return (
        <div ref={ref} style={{ position: 'relative', flex: 2 }}>
            <div
                style={{ padding: '8px 12px', backgroundColor: '#2a2a2a', border: `1px solid ${open ? '#bb86fc' : '#444'}`, borderRadius: '6px', color: selected ? '#e0e0e0' : '#666', fontSize: '13px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                onClick={() => setOpen(o => !o)}
            >
                <span>{selected ? selected.name : '— Search or create member —'}</span>
                <span style={{ color: '#666', fontSize: '10px' }}>▼</span>
            </div>

            {open && (
                <div style={dropStyle}>
                    <div style={{ padding: '6px 8px', borderBottom: '1px solid #444', position: 'sticky', top: 0, backgroundColor: '#2a2a2a' }}>
                        <input
                            autoFocus
                            style={{ width: '100%', padding: '6px 10px', backgroundColor: '#1a1a1a', border: '1px solid #555', borderRadius: '4px', color: '#e0e0e0', fontSize: '13px', boxSizing: 'border-box' }}
                            placeholder="Search members..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            onClick={e => e.stopPropagation()}
                        />
                    </div>
                    {value && (
                        <div style={optStyle(false)} onClick={() => { onChange(''); setOpen(false); setSearch(''); }}>
                            <span style={{ color: '#888' }}>— Clear selection —</span>
                        </div>
                    )}
                    {filtered.map(m => (
                        <div
                            key={m.id}
                            style={optStyle(String(m.id) === String(value))}
                            onClick={() => { onChange(String(m.id)); setOpen(false); setSearch(''); }}
                        >
                            {m.name}
                        </div>
                    ))}
                    <div
                        style={{ padding: '9px 12px', cursor: 'pointer', fontSize: '13px', color: '#bb86fc', borderTop: filtered.length > 0 ? '1px solid #444' : 'none', display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 500 }}
                        onClick={handleOpenModal}
                    >
                        <span style={{ fontSize: '16px', lineHeight: 1 }}>+</span>
                        {search.trim() ? `Create "${search.trim()}"` : 'Create new member'}
                    </div>
                </div>
            )}

            {showModal && (
                <QuickCreateMemberModal
                    initialName={modalName}
                    onCreated={handleCreated}
                    onClose={() => setShowModal(false)}
                />
            )}
        </div>
    );
}

// Quick-create member modal (used by MemberSearchSelect)
function QuickCreateMemberModal({ initialName, onCreated, onClose }) {
    const isMobile = useIsMobile();
    const [form, setForm] = useState({ name: initialName || '', hometown_city: '', hometown_state: '', hometown_country: '' });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const setField = (f, v) => setForm(x => ({ ...x, [f]: v }));

    const handleSave = async () => {
        if (!form.name.trim()) { setError('Name is required.'); return; }
        setSaving(true);
        setError(null);
        try {
            const res = await axios.post(`${config.API_URL}/members`, {
                name: form.name.trim(),
                hometown_city: form.hometown_city.trim() || null,
                hometown_state: form.hometown_state.trim() || null,
                hometown_country: form.hometown_country.trim() || null
            });
            onCreated(res.data.member);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to create member.');
            setSaving(false);
        }
    };

    // Spec: SHARC_Mobile_Responsive_Spec_Phase2_v1.0, §8.6 — City/State fields
    // stack to one column on mobile instead of splitting a ~281px-wide modal
    // interior into two ~135px inputs. The modal's own maxWidth: '90vw'
    // already handles sizing correctly — no change needed there.
    const ms = {
        overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.75)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' },
        modal: { backgroundColor: '#1e1e1e', border: '1px solid #444', borderRadius: '10px', padding: '28px', width: '420px', maxWidth: '90vw' },
        title: { fontSize: '18px', fontWeight: 700, color: '#ffffff', marginBottom: '6px' },
        hint: { fontSize: '13px', color: '#777', marginBottom: '20px' },
        label: { display: 'block', marginBottom: '5px', fontSize: '13px', color: '#aaa' },
        input: { width: '100%', padding: '9px 12px', backgroundColor: '#2a2a2a', border: '1px solid #444', borderRadius: '6px', color: '#e0e0e0', fontSize: '14px', boxSizing: 'border-box', marginBottom: '14px' },
        row2: {
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            gap: '12px',
            marginBottom: '14px',
        },
        actions: { display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' },
    };

    return (
        <div style={ms.overlay} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
            <div style={ms.modal}>
                <div style={ms.title}>Create Member</div>
                <div style={ms.hint}>Fill in the basics — you can add more detail from the member's profile later.</div>
                <label style={ms.label}>Name *</label>
                <input style={ms.input} value={form.name} onChange={e => setField('name', e.target.value)} placeholder="Full name or stage name" autoFocus />
                <label style={ms.label}>Hometown</label>
                <div style={ms.row2}>
                    <input style={{ ...ms.input, marginBottom: 0 }} value={form.hometown_city} onChange={e => setField('hometown_city', e.target.value)} placeholder="City" />
                    <input style={{ ...ms.input, marginBottom: 0 }} value={form.hometown_state} onChange={e => setField('hometown_state', e.target.value)} placeholder="State" />
                </div>
                <input style={ms.input} value={form.hometown_country} onChange={e => setField('hometown_country', e.target.value)} placeholder="Country" />
                {error && <div style={{ color: '#cf6679', fontSize: '13px', marginBottom: '12px' }}>{error}</div>}
                <div style={ms.actions}>
                    <button style={styles.cancelBtn} onClick={onClose}>Cancel</button>
                    <button style={styles.saveBtn} onClick={handleSave} disabled={saving}>{saving ? 'Creating...' : 'Create Member'}</button>
                </div>
            </div>
        </div>
    );
}

// Reusable member rows for lineup and show editors
function LineupMemberRows({ rows, allMembers, onUpdate, onAdd, onRemove, onMemberCreated }) {
    return (
        <>
            {rows.map((row, i) => (
                <div key={i} style={styles.formRow}>
                    <MemberSearchSelect
                        value={row.member_id}
                        allMembers={allMembers}
                        onChange={val => onUpdate(i, 'member_id', val)}
                        onMemberCreated={onMemberCreated}
                    />
                    <input
                        style={{ ...styles.smallInput, flex: 1 }}
                        placeholder="Role (e.g. Vocals/Guitar)"
                        value={row.role}
                        onChange={e => onUpdate(i, 'role', e.target.value)}
                    />
                    {rows.length > 1 && (
                        <button style={styles.removeBtn} onClick={() => onRemove(i)}>×</button>
                    )}
                </div>
            ))}
            <button style={styles.addRowBtn} onClick={onAdd}>+ Add Row</button>
        </>
    );
}

// Collapsible section wrapper
function Section({ title, count, children, defaultOpen = true }) {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <div style={styles.section}>
            <div style={styles.sectionHeader} onClick={() => setOpen(o => !o)}>
                <span style={styles.sectionTitle}>{title}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    {count !== undefined && <span style={styles.sectionCount}>{count} record{count !== 1 ? 's' : ''}</span>}
                    <span style={{ ...styles.chevron, transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
                </div>
            </div>
            {open && <div style={styles.sectionBody}>{children}</div>}
        </div>
    );
}

// Per-show member editor panel — loads current assignments then allows full replace
function ShowMemberEditor({ show, artistId, allMembers, onMemberCreated, onDone }) {
    const [members, setMembers] = useState([{ member_id: '', role: '' }]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        axios.get(`${config.API_URL}/artists/${artistId}/members`)
            .then(res => {
                const allShows = (res.data.member_history || []).flatMap(mh => mh.shows || []);
                const thisEvent = allShows.filter(s => s.event_id === show.event_id);
                const seen = new Set();
                const rows = [];
                thisEvent.forEach(s => {
                    if (!seen.has(s.member_id)) {
                        seen.add(s.member_id);
                        rows.push({ member_id: String(s.member_id), role: s.role || '' });
                    }
                });
                setMembers(rows.length > 0 ? rows : [{ member_id: '', role: '' }]);
            })
            .catch(() => setMembers([{ member_id: '', role: '' }]))
            .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show.event_id, artistId]);

    const handleSave = async () => {
        const valid = members.filter(m => m.member_id);
        setSaving(true);
        setError(null);
        try {
            await axios.put(
                `${config.API_URL}/events/${show.event_id}/artists/${artistId}/members`,
                { members: valid }
            );
            onDone();
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to save.');
            setSaving(false);
        }
    };

    if (loading) return <div style={{ color: '#666', fontSize: '13px', padding: '8px 0' }}>Loading...</div>;

    return (
        <div style={styles.showEditPanel}>
            <div style={styles.showEditTitle}>Edit members for this show</div>
            <LineupMemberRows
                rows={members}
                allMembers={allMembers}
                onUpdate={(i, field, val) => setMembers(m => m.map((x, idx) => idx === i ? { ...x, [field]: val } : x))}
                onAdd={() => setMembers(m => [...m, { member_id: '', role: '' }])}
                onRemove={(i) => setMembers(m => m.filter((_, idx) => idx !== i))}
                onMemberCreated={onMemberCreated}
            />
            {error && <div style={{ ...styles.errorMsg, marginTop: '8px' }}>{error}</div>}
            <div style={{ display: 'flex', gap: '10px', marginTop: '12px' }}>
                <button style={styles.saveBtn} onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
                <button style={styles.cancelBtn} onClick={onDone}>Cancel</button>
            </div>
        </div>
    );
}

// Member History sub-section
function MemberHistory({ memberHistory, artistId, allMembers, onMemberCreated, onRefresh }) {
    const [expanded, setExpanded] = useState({});
    const [editingShow, setEditingShow] = useState(null);

    if (!memberHistory || memberHistory.length === 0) {
        return <div style={styles.emptyState}>No member show history recorded yet.</div>;
    }

    const toggleExpand = (key) => setExpanded(e => ({ ...e, [key]: !e[key] }));

    return (
        <table style={styles.table}>
            <thead>
                <tr>
                    <th style={styles.th}>Member</th>
                    <th style={styles.th}>Roles</th>
                    <th style={styles.th}>Shows</th>
                    <th style={styles.th}></th>
                </tr>
            </thead>
            <tbody>
                {memberHistory.map((row) => {
                    const key = `${row.member_id}`;
                    const isOpen = expanded[key];
                    return (
                        <React.Fragment key={key}>
                            <tr>
                                <td style={styles.td}>
                                    <Link to={`/members/${row.member_slug}`} style={styles.link}>{row.member_name}</Link>
                                </td>
                                <td style={styles.td}>
                                    {row.roles
                                        ? row.roles.split(', ').map((r, i) => <span key={i} style={styles.rolePill}>{r}</span>)
                                        : <span style={{ color: '#555' }}>—</span>}
                                </td>
                                <td style={styles.td}><span style={styles.badge}>{row.show_count}</span></td>
                                <td style={styles.td}>
                                    <button style={styles.expandBtn} onClick={() => toggleExpand(key)}>
                                        {isOpen ? '▲ Hide shows' : '▼ View shows'}
                                    </button>
                                </td>
                            </tr>
                            {isOpen && (
                                <tr>
                                    <td colSpan={4} style={{ padding: '0 12px 12px 12px' }}>
                                        <div style={styles.expandedShows}>
                                            {row.shows && row.shows.length > 0 ? (
                                                <table style={{ ...styles.table, fontSize: '13px' }}>
                                                    <thead>
                                                        <tr>
                                                            <th style={styles.subTh}>Date</th>
                                                            <th style={styles.subTh}>Venue</th>
                                                            <th style={styles.subTh}>City</th>
                                                            <th style={styles.subTh}>State</th>
                                                            <th style={styles.subTh}>Country</th>
                                                            <th style={styles.subTh}>Role</th>
                                                            <th style={styles.subTh}></th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {row.shows.map(s => (
                                                            <React.Fragment key={s.event_id}>
                                                                <tr style={{ backgroundColor: editingShow === s.event_id ? '#12121e' : 'transparent' }}>
                                                                    <td style={styles.subTd}>
                                                                        <Link to={`/events/${s.event_slug}`} style={styles.link}>
                                                                            {s.event_date ? new Date(s.event_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' }) : '—'}
                                                                        </Link>
                                                                    </td>
                                                                    <td style={styles.subTd}>
                                                                        <Link to={`/venues/${s.venue_slug}`} style={styles.link}>{s.venue_name}</Link>
                                                                    </td>
                                                                    <td style={styles.subTd}>{s.city || '—'}</td>
                                                                    <td style={styles.subTd}>{s.state || '—'}</td>
                                                                    <td style={styles.subTd}>{s.country || '—'}</td>
                                                                    <td style={styles.subTd}>{s.role ? <span style={styles.rolePill}>{s.role}</span> : <span style={{ color: '#444' }}>—</span>}</td>
                                                                    <td style={styles.subTd}>
                                                                        <button
                                                                            style={{ ...styles.editBtn, fontSize: '11px', padding: '3px 8px' }}
                                                                            onClick={() => setEditingShow(editingShow === s.event_id ? null : s.event_id)}
                                                                        >
                                                                            {editingShow === s.event_id ? 'Cancel' : 'Edit Members'}
                                                                        </button>
                                                                    </td>
                                                                </tr>
                                                                {editingShow === s.event_id && (
                                                                    <tr>
                                                                        <td colSpan={7} style={{ padding: '0 0 8px 0' }}>
                                                                            <ShowMemberEditor
                                                                                show={s}
                                                                                artistId={artistId}
                                                                                allMembers={allMembers}
                                                                                onMemberCreated={onMemberCreated}
                                                                                onDone={() => { setEditingShow(null); onRefresh(); }}
                                                                            />
                                                                        </td>
                                                                    </tr>
                                                                )}
                                                            </React.Fragment>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            ) : <div style={{ color: '#666', fontSize: '13px' }}>No shows found.</div>}
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </React.Fragment>
                    );
                })}
            </tbody>
        </table>
    );
}

// Member Lineup sub-section
function MemberLineup({ lineups, allMembers, artistId, onRefresh, onMemberCreated }) {
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [editingLineupId, setEditingLineupId] = useState(null);

    const [createName, setCreateName] = useState('');
    const [createMembers, setCreateMembers] = useState([{ member_id: '', role: '' }]);
    const [createSaving, setCreateSaving] = useState(false);
    const [createError, setCreateError] = useState(null);

    const [editName, setEditName] = useState('');
    const [editMembers, setEditMembers] = useState([]);
    const [editSaving, setEditSaving] = useState(false);
    const [editError, setEditError] = useState(null);

    const handleCreate = async () => {
        if (!createName.trim()) { setCreateError('Lineup name is required.'); return; }
        const valid = createMembers.filter(m => m.member_id);
        if (valid.length === 0) { setCreateError('At least one member is required.'); return; }
        setCreateSaving(true);
        setCreateError(null);
        try {
            await axios.post(`${config.API_URL}/artists/${artistId}/lineups`, {
                name: createName.trim(), members: valid
            });
            setShowCreateForm(false);
            setCreateName('');
            setCreateMembers([{ member_id: '', role: '' }]);
            onRefresh();
        } catch (err) {
            setCreateError(err.response?.data?.error || 'Failed to save lineup.');
        } finally {
            setCreateSaving(false);
        }
    };

    const startEdit = (lu) => {
        setEditingLineupId(lu.id);
        setEditName(lu.name);
        setEditMembers(lu.members
            ? lu.members.map(m => ({ member_id: String(m.member_id), role: m.role || '' }))
            : [{ member_id: '', role: '' }]
        );
        setEditError(null);
    };

    const handleUpdate = async (lineupId) => {
        if (!editName.trim()) { setEditError('Lineup name is required.'); return; }
        const valid = editMembers.filter(m => m.member_id);
        if (valid.length === 0) { setEditError('At least one member is required.'); return; }
        setEditSaving(true);
        setEditError(null);
        try {
            await axios.put(`${config.API_URL}/artists/${artistId}/lineups/${lineupId}`, {
                name: editName.trim(), members: valid
            });
            setEditingLineupId(null);
            onRefresh();
        } catch (err) {
            setEditError(err.response?.data?.error || 'Failed to update lineup.');
        } finally {
            setEditSaving(false);
        }
    };

    const handleDelete = async (lineupId) => {
        if (!window.confirm('Delete this lineup?')) return;
        try {
            await axios.delete(`${config.API_URL}/artists/${artistId}/lineups/${lineupId}`);
            onRefresh();
        } catch (err) {
            alert('Failed to delete lineup.');
        }
    };

    return (
        <div>
            {lineups && lineups.length > 0 ? lineups.map(lu => (
                <div key={lu.id} style={styles.lineupCard}>
                    {editingLineupId === lu.id ? (
                        <div style={styles.editLineupForm}>
                            <div style={{ marginBottom: '12px' }}>
                                <input style={styles.input} placeholder="Lineup name" value={editName} onChange={e => setEditName(e.target.value)} />
                            </div>
                            <LineupMemberRows
                                rows={editMembers}
                                allMembers={allMembers}
                                onUpdate={(i, field, val) => setEditMembers(m => m.map((x, idx) => idx === i ? { ...x, [field]: val } : x))}
                                onAdd={() => setEditMembers(m => [...m, { member_id: '', role: '' }])}
                                onRemove={(i) => setEditMembers(m => m.filter((_, idx) => idx !== i))}
                                onMemberCreated={onMemberCreated}
                            />
                            {editError && <div style={{ ...styles.errorMsg, marginTop: '8px' }}>{editError}</div>}
                            <div style={{ display: 'flex', gap: '10px', marginTop: '14px' }}>
                                <button style={styles.saveBtn} onClick={() => handleUpdate(lu.id)} disabled={editSaving}>{editSaving ? 'Saving...' : 'Save Changes'}</button>
                                <button style={styles.cancelBtn} onClick={() => setEditingLineupId(null)}>Cancel</button>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div style={styles.lineupName}>{lu.name}</div>
                            <div>
                                {lu.members && lu.members.map(m => (
                                    <span key={m.member_id} style={styles.memberPill}>
                                        <Link to={`/members/${m.member_slug}`} style={styles.link}>{m.member_name}</Link>
                                        {m.role && <span style={{ color: '#888', fontSize: '11px' }}>{m.role}</span>}
                                    </span>
                                ))}
                            </div>
                            <div style={styles.lineupActions}>
                                <button style={styles.editBtn} onClick={() => startEdit(lu)}>Edit</button>
                                <button style={styles.deleteBtn} onClick={() => handleDelete(lu.id)}>Delete</button>
                            </div>
                        </>
                    )}
                </div>
            )) : (
                <div style={styles.emptyState}>No lineups saved yet.</div>
            )}

            {!showCreateForm ? (
                <button style={styles.addRowBtn} onClick={() => setShowCreateForm(true)}>+ Create Lineup</button>
            ) : (
                <div style={styles.addLineupForm}>
                    <div style={{ marginBottom: '12px' }}>
                        <input style={styles.input} placeholder="Lineup name (e.g. Classic 5-piece)" value={createName} onChange={e => setCreateName(e.target.value)} />
                    </div>
                    <LineupMemberRows
                        rows={createMembers}
                        allMembers={allMembers}
                        onUpdate={(i, field, val) => setCreateMembers(m => m.map((x, idx) => idx === i ? { ...x, [field]: val } : x))}
                        onAdd={() => setCreateMembers(m => [...m, { member_id: '', role: '' }])}
                        onRemove={(i) => setCreateMembers(m => m.filter((_, idx) => idx !== i))}
                        onMemberCreated={onMemberCreated}
                    />
                    {createError && <div style={{ ...styles.errorMsg, marginTop: '8px' }}>{createError}</div>}
                    <div style={{ display: 'flex', gap: '10px', marginTop: '14px' }}>
                        <button style={styles.saveBtn} onClick={handleCreate} disabled={createSaving}>{createSaving ? 'Saving...' : 'Save Lineup'}</button>
                        <button style={styles.cancelBtn} onClick={() => { setShowCreateForm(false); setCreateError(null); }}>Cancel</button>
                    </div>
                </div>
            )}
        </div>
    );
}

// Shows Without Members sub-section
function ShowsWithoutMembers({ shows, lineups, artistId, onRefresh }) {
    const [selectedLineup, setSelectedLineup] = useState('');
    const [checkedShows, setCheckedShows] = useState({});
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState(null);

    const toggleCheck = (id) => setCheckedShows(c => ({ ...c, [id]: !c[id] }));
    const toggleAll = () => {
        const allChecked = shows.every(s => checkedShows[s.event_id]);
        const next = {};
        shows.forEach(s => { next[s.event_id] = !allChecked; });
        setCheckedShows(next);
    };

    const handleAssign = async () => {
        if (!selectedLineup) { setMessage({ type: 'error', text: 'Please select a lineup.' }); return; }
        const showIds = shows.filter(s => checkedShows[s.event_id]).map(s => s.event_id);
        if (showIds.length === 0) { setMessage({ type: 'error', text: 'Please select at least one show.' }); return; }
        setSaving(true);
        setMessage(null);
        try {
            const res = await axios.post(`${config.API_URL}/artists/${artistId}/lineups/${selectedLineup}/assign`, { show_ids: showIds });
            const { assigned, skipped } = res.data;
            setMessage({ type: 'success', text: `Assigned to ${assigned} show${assigned !== 1 ? 's' : ''}${skipped > 0 ? ` (${skipped} skipped — already had members)` : ''}.` });
            setCheckedShows({});
            onRefresh();
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to assign lineup.' });
        } finally {
            setSaving(false);
        }
    };

    if (!shows || shows.length === 0) {
        return <div style={styles.emptyState}>All shows have members assigned. 🎉</div>;
    }

    const checkedCount = shows.filter(s => checkedShows[s.event_id]).length;

    return (
        <div>
            <div style={styles.assignBar}>
                <select style={styles.select} value={selectedLineup} onChange={e => setSelectedLineup(e.target.value)}>
                    <option value="">— Select a lineup to apply —</option>
                    {lineups && lineups.map(lu => (
                        <option key={lu.id} value={lu.id}>{lu.name} ({lu.members ? lu.members.length : 0} members)</option>
                    ))}
                </select>
                <button style={styles.saveBtn} onClick={handleAssign} disabled={saving || !checkedCount}>
                    {saving ? 'Saving...' : `Assign to ${checkedCount} show${checkedCount !== 1 ? 's' : ''}`}
                </button>
                <button style={styles.cancelBtn} onClick={() => setCheckedShows({})}>Clear</button>
            </div>
            {message && (
                <div style={message.type === 'success' ? styles.successMsg : styles.errorMsg}>{message.text}</div>
            )}
            <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input type="checkbox" style={styles.checkbox} checked={shows.every(s => checkedShows[s.event_id])} onChange={toggleAll} />
                <span style={{ color: '#888', fontSize: '13px' }}>Select all ({shows.length} shows)</span>
            </div>
            <div style={styles.showsCheckList}>
                {shows.map(s => (
                    <div key={s.event_id} style={styles.checkRow}>
                        <input type="checkbox" style={styles.checkbox} checked={!!checkedShows[s.event_id]} onChange={() => toggleCheck(s.event_id)} />
                        <Link to={`/events/${s.event_slug}`} style={styles.link}>
                            {s.event_date ? new Date(s.event_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' }) : '—'}
                        </Link>
                        <span style={{ color: '#ccc' }}>{s.venue_name}</span>
                        <span style={{ color: '#888' }}>{[s.city, s.state, s.country].filter(Boolean).join(', ')}</span>
                        {s.festival_name && <span style={{ color: '#666', fontStyle: 'italic', fontSize: '13px' }}>{s.festival_name}</span>}
                    </div>
                ))}
            </div>
        </div>
    );
}

// Main exported component
export default function ArtistMembersTab({ artistId }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [localAllMembers, setLocalAllMembers] = useState(null);

    const fetchData = useCallback(() => {
        setLoading(true);
        axios.get(`${config.API_URL}/artists/${artistId}/members`)
            .then(res => setData(res.data))
            .catch(() => setError('Failed to load member data.'))
            .finally(() => setLoading(false));
    }, [artistId]);

    useEffect(() => { fetchData(); }, [fetchData]);

    if (loading) return <div style={{ color: '#666', padding: '24px 0', textAlign: 'center' }}>Loading members...</div>;
    if (error) return <div style={{ color: '#cf6679', padding: '24px 0', textAlign: 'center' }}>{error}</div>;
    if (!data) return null;

    const { member_history, lineups, shows_without_members, all_members } = data;
    const allMembersResolved = localAllMembers || all_members || [];

    const handleMemberCreated = (newMember) => {
        setLocalAllMembers(prev => {
            const base = prev || all_members || [];
            if (base.find(m => m.id === newMember.id)) return base;
            return [...base, { id: newMember.id, name: newMember.name, slug: newMember.slug }].sort((a, b) => a.name.localeCompare(b.name));
        });
    };

    return (
        <div>
            <Section title="Member History" count={member_history ? new Set(member_history.map(r => r.member_id)).size : 0} defaultOpen={false}>
                <MemberHistory
                    memberHistory={member_history}
                    artistId={artistId}
                    allMembers={allMembersResolved}
                    onMemberCreated={handleMemberCreated}
                    onRefresh={fetchData}
                />
            </Section>

            <Section title="Member Lineups" count={lineups ? lineups.length : 0} defaultOpen={false}>
                <MemberLineup
                    lineups={lineups}
                    allMembers={allMembersResolved}
                    artistId={artistId}
                    onMemberCreated={handleMemberCreated}
                    onRefresh={fetchData}
                />
            </Section>

            <Section title="Shows Without Members" count={shows_without_members ? shows_without_members.length : 0} defaultOpen={false}>
                <ShowsWithoutMembers
                    shows={shows_without_members}
                    lineups={lineups}
                    artistId={artistId}
                    onRefresh={fetchData}
                />
            </Section>
        </div>
    );
}
